import React, { useEffect, useState } from 'react'
import cx from 'classnames'
import { Select, Tag } from 'antd'
import { WrappedFieldProps } from 'redux-form'
import { InputProps } from 'antd/lib/input'
import { FormItemLabelProps } from 'antd/lib/form/FormItemLabel'
import { useDispatch } from 'react-redux'
import { map } from 'lodash'
import { useTranslation } from 'react-i18next'
import { PlusOutlined } from '@ant-design/icons'
import i18next from 'i18next'
// eslint-disable-next-line import/no-cycle
import { getJobsStoreItems, IJobsStoreItemsPayload, JobStoreItem } from '../../../reducers/jobs/actions'
// eslint-disable-next-line import/no-cycle
import { STORE_TYPE } from '../../stores/Stores'
// eslint-disable-next-line import/no-cycle
import { colorHash } from '../StoreItems'
// eslint-disable-next-line import/no-cycle
import { getPriorityForStoreType } from '../components/AddressWithStocksTable'

type Props = WrappedFieldProps & InputProps & FormItemLabelProps & any & {
	label?: string,
	filterStoreWithZeroStock: boolean,
	showLabel?: string,
	placeholder?: string,
	customClass?: string,
	customInputClass?: string,
	customLabelClass?: string,
	required?: boolean,
	allowClear?: boolean
} & {
	onlyVirtualStores?: boolean,
	storeItemID?: number,
	storeType?: STORE_TYPE | null,
	// zamknuté pole - hodnota je pevne daná kontextovým skladom, needituje sa
	locked?: boolean,
	/**
	 * Kontextový sklad aj s adresou. Zoznam adries sa načítava po stránkach a "Adresa z" filtruje
	 * sklady s nulovým stavom - kontextový sklad preto v ňom vôbec nemusí byť. Túto adresu použijeme
	 * v zamknutom poli aj ako náhradnú Option, aby Select nezobrazil holé ID.
	 */
	contextOption?: { id: number, label: string, storeType?: STORE_TYPE | null } | null,
	// obsah vykreslený vedľa labelu (ikona zámku)
	labelSuffix?: React.ReactNode,
	/**
	 * Zavolá sa so zvolenou adresou (vrátane storeItemsCount) - nielen pri kliku, ale aj keď sa
	 * dotiahne zoznam a v ňom sa nájde už zvolená hodnota. Používa sa pri "Pripísať cez",
	 * kde treba poznať stav položky na sklade.
	 */
	onOptionSelected?: (option: JobStoreItem | null) => void,
	/**
	 * Zavolá sa po načítaní zoznamu s adresami, ktoré sa reálne ponúkajú (už po filtri nulových
	 * stavov). Používa sa na predvyplnenie skladu v poli "Pripísať cez" - samotné rozhodnutie,
	 * ktorá adresa sa predvyplní, patrí formuláru, nie tomuto poľu.
	 */
	onOptionsLoaded?: (options: JobStoreItem[]) => void
}

const { Option } = Select

const JobSelectField = (props: Props) => {
	const {
		input,
		label,
		filterStoreWithZeroStock,
		placeholder,
		showLabel,
		customClass,
		customLabelClass,
		meta: { touched, error },
		type,
		disabled,
		onInput,
		onKeyUp,
		suffix,
		required,
		min,
		onBlur,
		width,
		multiple,
		onlyVirtualStores,
		storeItemID,
		storeType,
		locked,
		contextOption,
		labelSuffix,
		onOptionSelected,
		onOptionsLoaded,
		...rest
	} = props

	const dispatch: any = useDispatch()

	const { t } = useTranslation('pages')

	const [query, setQuery] = useState({
		search: '',
		limit: 100,
		page: 1
	})

	const [jobStoreItemsList, setStoreItemsList] = useState<IJobsStoreItemsPayload | null>(null)

	useEffect(() => {
		// zamknuté pole zobrazuje label z kontextu, zoznam adries netreba načítavať
		if (locked) {
			return
		}
		dispatch(getJobsStoreItems({
			limit: query.limit,
			page: query.page,
			queryParams: {
				search: query.search,
				storeItemID,
				storeType: storeType || null
			}
		}, (data: IJobsStoreItemsPayload) => {
			setStoreItemsList(data)
		}))
	}, [query.search, storeType, locked])

	const jobs = jobStoreItemsList?.jobs?.filter(
		(job) => job.storeItemsCount > 0 || !filterStoreWithZeroStock
	) || []

	// ponúkané adresy sa zmenili - nech sa nadradený formulár vie rozhodnúť, čo predvyplniť
	useEffect(() => {
		if (onOptionsLoaded && jobStoreItemsList) {
			onOptionsLoaded(jobs)
		}
	}, [jobStoreItemsList])

	const checkValue = () => {
		if (input.value) {
			return input.value
		}

		if (multiple) {
			return []
		}

		return input.value
	}

	const onInputChange = (value: any) => {
		if (multiple) {
			input.onChange(value || [])
		} else {
			input.onChange(value || null)
			if (onOptionSelected) {
				onOptionSelected(jobStoreItemsList?.jobs?.find((job) => job.id === value) || null)
			}
		}
	}

	// zoznam sa dotiahne až po výbere (alebo po zmene hľadania) - ohlás už zvolenú adresu aj vtedy
	useEffect(() => {
		if (!onOptionSelected || multiple || !input.value) {
			return
		}
		const selected = jobStoreItemsList?.jobs?.find((job) => job.id === input.value)
		if (selected) {
			onOptionSelected(selected)
		}
	}, [jobStoreItemsList, input.value])

	const onSearch = (value: any) => {
		setQuery(
			{
				...query,
				search: value,
				page: 1
			}
		)
	}

	const contextTagLabel = getPriorityForStoreType(contextOption?.storeType || null, t)

	// kontextový sklad nemusí byť v načítanej stránke adries - doplň ho, nech Select nezobrazí holé ID
	const showContextOption = !!contextOption
		&& input.value === contextOption.id
		&& !jobs.some((job) => job.id === contextOption.id)

	return (
		<div className={ cx('input-wrapper', { error: touched && error }, customClass) }>
			<label className={ cx(customLabelClass, { required }) }>
				{label || t('components:Adresa')}
				{ labelSuffix && <span className={'job-select-label-suffix'}>{ labelSuffix }</span> }
			</label>
			{ locked ? (
				<div className={ 'input-field min-w-full' }>
					<div className={ 'job-select-locked' }>
						<span className={ 'job-select-locked-address' }>{ contextOption?.label }</span>
						{ contextTagLabel && <Tag color={ colorHash(contextOption?.storeType || '') }>{ contextTagLabel }</Tag> }
					</div>
				</div>
			) : (
				<div className={ 'input-field min-w-full' }>
					<Select
						{ ...input }
						value={ checkValue() }
						labelInValue={ false }
						placeholder={ placeholder }
						type={ type }
						disabled={ disabled }
						className={ 'min-w-full' }
						onInput={ onInput }
						onKeyUp={ onKeyUp }
						required={ required }
						min={ min }
						onBlur={ onBlur }
						suffix={ suffix }
						filterOption={false}
						style={ { width } }
						onSearch={ onSearch }
						onChange={ onInputChange }
						notFoundContent={ <div><PlusOutlined/> { i18next.t('pages:Vytvorte si virtualny sklad') } <b style={ { color: 'black' } }></b></div> }
						{ ...rest }
					>
						{ showContextOption && contextOption && (
							<Option
								key={ contextOption.id }
								value={ contextOption.id }
								title={ contextOption.label }
								label={ contextOption.label }
							>
								<div className={'w-full flex justify-between'}>
									<span className={'w-16'}>{ contextOption.label }</span>
									{ contextTagLabel && <Tag color={colorHash(contextOption.storeType || '')}>{ contextTagLabel }</Tag> }
								</div>
							</Option>
						) }
						{map(jobs, (option) => (
							<Option
								key={ option.id || 0 }
								value={ option.id || 0 }
								title={ option.address}
								label={ option.address }
							>
								<div className={'w-full flex justify-between'}>
									<span className={'w-16'}>{ option.address }</span>
									{ option.storeType && <Tag color={colorHash(option.storeType || '')}>{getPriorityForStoreType(option.storeType, t)}</Tag> }
									{ option.storeItemsCount && <span>{ option.storeItemsCount }</span>}
								</div>
							</Option>
						))
						}
					</Select>
				</div>
			) }
			<div className={ 'tooltip-error' }>
				{ touched ? error : '' }
			</div>
		</div>
	)
}

export default JobSelectField
